import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Stream } from '../types/stream';
import { StreamService } from '../services/stream_service';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';

import { Util } from '../util/util';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import { CacheService } from '../services/cache_service';
import { ValidateCustomUrlDuplication } from '../validators/customUrlDuplication.validator';
import { GtmService } from '../services/gtm_service';
import { UserService } from '../services/user_service';
import { StreamKind } from '../types/stream_kind';
import { User } from '../types/user';

@Component({
    selector: 'stream-creator',
    templateUrl: './stream-creator.component.html',
    styleUrls: ['./stream-creator.component.scss']
})
export class StreamCreatorComponent implements OnInit, OnDestroy {
    @Input()
    public stream: Stream;

    @Input()
    public showCloseModalButton = false;

    @Input()
    public editView = false;

    @Output()
    public updatedStreamEmitter: EventEmitter<Stream> = new EventEmitter();

    @Output()
    public closeStreamModalEmitter: EventEmitter<any> = new EventEmitter();

    @Output()
    public cancelEditingEmmiter: EventEmitter<boolean | string> = new EventEmitter();

    public streamForm: UntypedFormGroup;
    public thumbnailErrors = {
        limit: false,
        other: ''
    };

    public currentStreamThumb: any;
    public streamTypes = [];
    public selectedStreamTypeKind: any;
    public savingStreamProgress = false;
    public addressLocation: any = {};
    public enableCustomUrl = false;

    public subscriptionsObject = [];
    public validPriceRange = {
        month: {
            min: 1,
            max: 1000
        },
        year: {
            min: 5,
            max: 12000
        }
    };

    public streamMemebers = {
        list: [],
        loading: false,
        show: false
    };

    public blockedUsersModalMessage = {
        type: '',
        message: ''
    };

    public memberInviteForm: UntypedFormGroup;
    public alert: {type: string; message: string; timeout?: number, floating?: boolean};
    public sendInvitationLoading = false;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    public StreamKind: typeof StreamKind = StreamKind;

    private thumbnailMaxSizeKb = 9146000;
    private thumbnailFileToUpload: any;
    private subscPlanTypes = /*[ 'DAILY', 'MONTHLY', 'YEARLY' ]*/['MONTHLY', 'YEARLY'];
    private user: User;

    constructor(
        private utilService: Util,
        private streamService: StreamService,
        private userService: UserService,
        private cacheService: CacheService,
        private fb: UntypedFormBuilder,
        private gtmService: GtmService,
        private modalService: NgbModal) { }

    public ngOnInit(): void {
        this.user = this.userService.curUser;
        this.updateValidPriceRange();
        this.createStreamForm();
        this.getStreamTypes();
        this.sortSubscriptionsObject();

        if (this.stream) {
            this.updateStreamData();
            this.getStreamPlans();

            if (this.stream.address) {
                this.addressLocation = this.stream.address;
            }

            if (
                this.stream.kind === StreamKind.OpenCommunity ||
                this.stream.kind === StreamKind.InviteOnlyCommunity ||
                this.stream.kind === StreamKind.PrivateCommunity ||
                this.stream.kind === StreamKind.InviteOnly) {
                    this.toggleCommunityMembersSection(true);
            }
        }

        this.enableCustomUrl = this.stream && this.stream.shortURLPath ? true : false;
        this.streamForm.get('customUrl')[this.enableCustomUrl ? 'enable' : 'disable']();
        this.streamForm.updateValueAndValidity();
    }

    public selectStreamType(stream: any): void {
        this.selectedStreamTypeKind = stream.kind;
        this.streamForm.markAsTouched({ onlySelf: true });
        for (const s of this.streamTypes) {
            if (s.kind === stream.kind) {
                s.selected = true;
                if (this.stream) {
                    this.stream.kind = s.kind;
                    if (s.kind === 1 || s.kind === 2 || s.kind === 3 || s.kind === 5) {
                        this.toggleCommunityMembersSection(true);
                    } else {
                        this.toggleCommunityMembersSection(false);
                    }
                }
            } else {
                s.selected = false;
            }
        }
    }

    public changeSubscriptionValue(event: any, plan: any): void {
        const inputValue = event.target.value;
        const convertedPrice = this.transformDollarsToCents(inputValue);

        for (const s of this.subscriptionsObject) {
            if (s.plan === plan.plan) {
                s.inputValue = inputValue;
                s.valid = (convertedPrice >= this.validPriceRange[s.planType.toString().toLowerCase()].min &&
                convertedPrice <= this.validPriceRange[s.planType.toString().toLowerCase()].max);

                if (s.valid && this.stream) {
                    s.edited = true;
                }
            }
        }
    }

    public saveStreamPlan(plan: any): void {
        if (plan.valid) {
            plan.active = 1;
            plan.price = plan.inputValue.toString().indexOf('$') >= 0 ?
                this.transformDollarsToCents(plan.inputValue) : plan.price;
        }
    }

    public toggleStreamPlan(plan: any): void {
        if (plan.valid) {
            plan.active = plan.active === 0 ? 1 : 0;
            plan.price = plan.inputValue.toString().indexOf('$') >= 0 ?
                this.transformDollarsToCents(plan.inputValue) : plan.price;
        }
    }

    public deactivateStreamPlan(plan: any): void {
        plan.active = 0;
    }

    public saveStream(): void {
        this.validateAllFormFields(this.streamForm);
        if (this.streamForm.valid) {
            const requests = [];
            this.savingStreamProgress = true;

            if (!this.stream) {
                this.streamService.createNewStream().then((newStream: Stream) => {
                    console.log('empty stream object if creating', newStream);
                    // update all data that where added in create form
                    this.stream = newStream;

                    const streamFormData = {
                        name: this.streamForm.get('streamName').value,
                        description: this.streamForm.get('streamDescription').value,
                        copyright: this.streamForm.get('streamCopyrights').value || '',
                        kind: this.selectedStreamTypeKind || this.stream.kind || 0
                    };

                    requests.push(this.streamService.updateUserStream(this.stream.id, streamFormData));

                    if (this.thumbnailFileToUpload && this.stream.id) {
                        requests.push(this.streamService.uploadThumbnail(this.thumbnailFileToUpload, this.stream.id));
                    }

                    for (const s of this.subscriptionsObject) {
                        if (s.valid && s.price >= 0) {
                            requests.push(this.streamService.putPrice(
                                this.stream.id,
                                s.price.toString(),
                                s.planType,
                                s.active
                            ));
                        }
                    }

                    if (this.addressLocation.address) {
                        requests.push(this.streamService.updateAddress(this.stream.id, this.addressLocation.address));
                    } else if (this.addressLocation.latitude && this.addressLocation.longitude) {
                        requests.push(this.streamService.updateLatitudeLongitude(
                            this.stream.id,
                            this.addressLocation.latitude,
                            this.addressLocation.longitude));
                    }

                    if (this.streamForm.get('customUrl').value && this.streamForm.get('customUrl').valid && this.enableCustomUrl) {
                        requests.push(this.streamService.saveCustomUrl(this.stream.id, this.streamForm.get('customUrl').value));
                    }

                    Promise.all(requests).then(result => {
                        this.streamService.getStreamInfoObservable(this.stream.id, true).subscribe((stream: Stream) => {
                            if (this.thumbnailFileToUpload) {
                                stream.iconImageUrl = this.utilService.uncacheUrl(stream.iconImageUrl);
                                delete this.thumbnailFileToUpload;
                            }

                            this.gtmService.pushTag({
                                event: 'stream-created',
                                streamId: stream.id,
                                streamName: stream.name
                            });

                            this.cacheService.setStreamInfo(stream);
                            this.streamService.getUserStreams(true);
                            this.savingStreamProgress = false;
                            this.stream = stream;

                            this.streamService.userStreamCreated(stream);
                            this.updatedStreamEmitter.emit(this.stream);

                            if (this.showCloseModalButton) {
                                this.closeModal();
                            }
                        });
                    }, creatingError => {
                        console.log('There was error while updating created new stream', creatingError);
                        this.savingStreamProgress = false;
                    });
                }, err => {
                    console.error('There was error while creating new stream', err);
                    this.savingStreamProgress = false;
                });
            } else {
                const streamFormData = {
                    name: this.streamForm.get('streamName').value,
                    description: this.streamForm.get('streamDescription').value,
                    copyright: this.streamForm.get('streamCopyrights').value || '',
                    kind: this.selectedStreamTypeKind || this.stream.kind || 0
                };

                requests.push(this.streamService.updateUserStream(this.stream.id, streamFormData));

                if (this.thumbnailFileToUpload && this.stream.id) {
                    requests.push(this.streamService.uploadThumbnail(this.thumbnailFileToUpload, this.stream.id));
                }

                for (const s of this.subscriptionsObject) {
                    if (s.valid && s.price >= 0) {
                        requests.push(this.streamService.putPrice(
                            this.stream.id,
                            s.price.toString(),
                            s.planType,
                            s.active
                        ));
                    }
                }

                if (this.addressLocation.address) {
                    requests.push(this.streamService.updateAddress(this.stream.id, this.addressLocation.address));
                } else if (this.addressLocation.latitude && this.addressLocation.longitude) {
                    requests.push(this.streamService.updateLatitudeLongitude(
                        this.stream.id,
                        this.addressLocation.latitude,
                        this.addressLocation.longitude));
                }

                if (this.streamForm.get('customUrl').value && this.streamForm.get('customUrl').valid && this.enableCustomUrl) {
                    requests.push(this.streamService.saveCustomUrl(this.stream.id, this.streamForm.get('customUrl').value));
                }

                if (!this.enableCustomUrl && this.stream.shortURLPath) {
                    requests.push(this.streamService.removeCustomUrl(this.stream.id));

                }

                Promise.all(requests).then(result => {
                    this.streamService.getStreamInfoObservable(this.stream.id, true).subscribe((stream: Stream) => {
                        if (this.thumbnailFileToUpload) {
                            stream.iconImageUrl = this.utilService.uncacheUrl(stream.iconImageUrl);
                            delete this.thumbnailFileToUpload;
                        }

                        this.cacheService.setStreamInfo(stream);
                        this.streamService.getUserStreams(true);
                        this.savingStreamProgress = false;
                        this.stream = stream;
                        this.updatedStreamEmitter.emit(this.stream);

                        if (this.showCloseModalButton) {
                            this.closeModal();
                        }
                    });
                }, creatingError => {
                    console.log('There was error while updating updating stream', creatingError);
                    this.savingStreamProgress = false;
                });
            }

        }
    }


    public geoLocatorEvent(event: Event) {
        this.addressLocation = Object.assign(this.addressLocation, event);
    }

    public setStreamThumbnail(e: any): void {
        this.thumbnailErrors.limit = false;
        this.thumbnailErrors.other = '';

        const newImg = e['dataURL'];
        const imgSize = newImg.replace(/=/g, '').length * 0.75;

        if (imgSize > this.thumbnailMaxSizeKb) {
          this.thumbnailErrors.limit = true;
        } else {
          fetch(newImg)
            .then(res => res.blob())
            .then(blob => {
              const fileName = e.name ? e.name : `${new Date().getTime()}.${blob.type.split('/')[1]}`;
              const f = new File([blob], fileName, {type: blob.type});

              this.utilService.downscaleImage(f, 1146000).then(downscaledImg => {
                console.log('downscaleImage result:', downscaledImg);
                this.thumbnailFileToUpload = downscaledImg;
              }, err => {
                this.thumbnailFileToUpload = f;
              });

            });

          this.currentStreamThumb = newImg;
        }
    }

    public onChangeCustomUrl(event: Event): void {
        this.enableCustomUrl = !this.enableCustomUrl;

        this.streamForm.get('customUrl')[this.enableCustomUrl ? 'enable' : 'disable']();
        this.streamForm.updateValueAndValidity();
    }

    public closeModal(): void {
        this.clearModalData();
        this.closeStreamModalEmitter.emit();
    }

    public removeUser(member: any): void {
        const modalOptions: NgbModalOptions = {};
        const refModalConf = this.modalService.open(ConfirmationModalComponent, modalOptions);

        refModalConf.componentInstance.confirmationModalTitle = 'Remove user from members list';
        refModalConf.componentInstance.confirmationModalMsg = 'Do you want to remove this user from the community member list?';

        refModalConf.result.then(resp => {
            if (resp) {
                this.streamService.removeCommunityMember(this.stream.id, member.userId).then(res => {
                this.setAlertMessage({
                    type: 'success',
                    message: 'User has been removed from community members list.',
                    timeout: 10000,
                    floating: true
                });

                this.getCommmunityMembersList();
                });
            } else {
                return false;
            }
        })
        .catch(err => {
            console.log(err);
        });
    }

    public sendMemberInvitation(): void {
        this.validateAllFormFields(this.memberInviteForm);
        if (this.memberInviteForm.valid) {
            const memberEmail = this.memberInviteForm.get('email').value;
            this.sendInvitationLoading = true;

            this.streamService.inviteToCommunity(this.stream.id, memberEmail).then(resp => {
                this.setAlertMessage({
                    type: 'success',
                    message: 'Invitation has been sent to the e-mail owner.',
                    timeout: 10000,
                    floating: true
                });

            this.sendInvitationLoading = false;
            this.getCommmunityMembersList();
        }, err => {
            console.error('inviteToCommunity api error', err);

            const errorCode = (err.error && err.error.code) ? err.error.code : null;
            let message: string;

            switch (errorCode) {
                case 'ALREADY_EXISTS':
                message = 'This user is already a member. Please try to add different e-mail.';
                break;
                case 'DOES_NOT_EXIST':
                message = 'This user doesn`t exist.';
                break;
                default:
                message = err.error.message || 'There was an unexpected error. Please try again.';
                break;
            }

            this.setAlertMessage({
                type: 'error',
                message,
                timeout: 10000,
                floating: true
            });

            this.sendInvitationLoading = false;
            });
        }
    }

    public cancelEdition(): void {
        this.cancelEditingEmmiter.emit('cancel');
    }

    public savePlanSeparately(plan): void {
        if (plan.valid) {
            plan.price = plan.inputValue.toString().indexOf('$') >= 0 ?
                this.transformDollarsToCents(plan.inputValue) : plan.price;
            plan.saving = true;

            this.streamService.putPrice(
                this.stream.id,
                plan.price.toString(),
                plan.planType,
                plan.active
            ).then(() => {
                this.setAlertMessage({
                    type: 'success',
                    message: 'The subscription price has changed successfully.',
                    timeout: 5000,
                    floating: true
                });

                plan.saving = false;
                plan.edited = false;
            }, err => {
                console.error('There was an error while saving the subscription price. Please try again.', err);
                this.setAlertMessage({
                    type: 'error',
                    message: 'There was an error while saving the subscription price. Please try again.',
                    timeout: 5000,
                    floating: true
                });

                plan.saving = false;
            });
        }
    }

    public ngOnDestroy(): void {
        this.clearModalData();
    }

    private toggleCommunityMembersSection(show?: boolean): void {
        this.alert = {
            type: '',
            message: '',
            timeout: 0
        };

        if (show !== this.streamMemebers.show) {
            if (show === true) {
                this.streamMemebers.show = true;
                this.createInviteMemberForm();
                this.getCommmunityMembersList();
            } else {
                console.log('hide');
                this.streamMemebers.show = false;
            }
        }
    }

    private createInviteMemberForm(): void {
        this.memberInviteForm = this.fb.group({
            email: [null, [
            Validators.required,
            Validators.email]
            ]
        }, {updateOn: 'submit'});

        this.memberInviteForm.markAsUntouched();
    }

    private getCommmunityMembersList(): void {
        this.streamMemebers.list = null;
        this.streamMemebers.loading = true;
        this.streamService.getStreamMembers(this.stream.id).then(members => {
            if (members && members.length) {
                const membersProfilesApis = [];

                for (const u of members) {
                    membersProfilesApis.push(this.userService.getUserProfile(u.userId));
                }

                Promise.all(membersProfilesApis).then(users => {
                    for (const m of members) {
                        const userMatch = users.filter(u => u.id === m.userId);

                        if (userMatch && userMatch[0]) {
                            m.user = userMatch[0];
                        }
                    }

                    this.streamMemebers.list = members;
                    this.streamMemebers.loading = false;
                });
            } else {
                this.streamMemebers.list = [];
                this.streamMemebers.loading = false;
            }
        });
    }

    private setAlertMessage(options: {type: string, message: string, timeout?: number, floating?: boolean}): void {
        this.alert = {
            type: options.type,
            message: options.message,
            timeout: options.timeout ?? 5000,
            floating: options.floating ?? false
        };
    }

    private createStreamForm(): void {
        this.streamForm = this.fb.group({
            streamName: ['', [Validators.required, Validators.maxLength(50)]],
            streamDescription: ['', [
                Validators.required,
                Validators.maxLength(600)]],
            streamCopyrights: '',
            customUrl: ['', [
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(500),
                Validators.pattern('^[\\w+-]+$')],
                ValidateCustomUrlDuplication(this.streamService, {id: this.stream ? this.stream.id : '', type: 'stream'})]
        });
    }

    private updateStreamData(): void {
        this.streamForm.patchValue({
            streamName: this.stream.name || '',
            streamDescription: this.stream.description || '',
            streamCopyrights: this.stream.copyright || '',
            customUrl: this.stream.shortURLPath || '',
        });
    }

    private getStreamTypes(): void {
        this.streamService.getStreamTypes().then(res => {
            this.streamTypes = res;
            this.getSelectedStreamType();
          });
    }

    private getSelectedStreamType(): void {
        if (this.stream && this.stream.kind) {
            for (const s of this.streamTypes) {
                if (s.kind === this.stream.kind) {
                    s.selected = true;
                    this.selectedStreamTypeKind = this.stream.kind;
                }
            }
        } else if (!this.stream || !this.stream.id || this.stream.kind === 0) {
            this.streamTypes[0].selected = true;
            this.selectedStreamTypeKind = 0;
            if (this.stream) {
                this.stream.kind = 0;
            }
        }
    }

    private resetStreamType(): void {
        for (const s of this.streamTypes) {
            s.selected = false;
        }

        delete this.selectedStreamTypeKind;
    }

    private sortSubscriptionsObject(): void {
        for (const s of this.subscPlanTypes) {
            this.subscriptionsObject.push({
                id: '',
                plan: s,
                planType: this.getPlanTypeLabel(s),
                price: this.getPlanMinPrice(s),
                minPrice: this.getPlanMinPrice(s),
                maxPrice: this.getPlanMaxPrice(s),
                inputValue: this.getUserMinPlanPrice(s),
                edited: false,
                saving: false,
                valid: true,
                active: 0
            });
        }
    }

    private getStreamPlans(): void {
        this.streamService.getStreamSubscriptionPlans(this.stream.id.toString()).then((plans: any) => {
            for (const so of this.subscriptionsObject) {
                const planType = so.planType;

                for (const s of plans) {
                    if (planType === s.periodStr) {
                        so.price = s.price;
                        so.inputValue = this.transformCentsToDollars(s.price);
                        so.active = s.active;
                        so.defaultState = s.active;
                        so.defaultPrice = s.price;
                    }
                }
            }
        });
    }

    private getPlanTypeLabel(plan: string): string {
        switch (plan) {
            case 'MONTHLY':
                return 'MONTH';
            case 'YEARLY':
                return 'YEAR';
        }
    }

    private getPlanMinPrice(plan: string): number {
        switch (plan) {
            case 'MONTHLY':
                return this.user.permissions.channelSubMinPriceMonthly ? this.user.permissions.channelSubMinPriceMonthly : 0;
            case 'YEARLY':
                return this.user.permissions.channelSubMinPriceAnnual ? this.user.permissions.channelSubMinPriceAnnual : 0;
            default:
            return 0.01;
        }
    }

    private getPlanMaxPrice(plan: string): number {
        switch (plan) {
            case 'MONTHLY':
                return 10;
            case 'YEARLY':
                return 120;
            default:
            return 10;
        }
    }

    private transformDollarsToCents(value: any): number {
        const noDollarValue = value ? value.toString().split('$').join('') : '';
        return Math.round(noDollarValue.split(',').join('') * 100);
    }

    private transformCentsToDollars(cents: number): string {
        const divisor = Math.pow(10, 2);
        return ('$' + (parseFloat(cents.toString()) / divisor).toFixed(2)).trim();
    }

    private validateAllFormFields(formGroup: UntypedFormGroup): void {
        Object.keys(formGroup.controls).forEach(field => {
            const control = formGroup.get(field);
            if (control instanceof UntypedFormControl) {
                control.markAsTouched({ onlySelf: true });
            } else if (control instanceof UntypedFormGroup) {
                this.validateAllFormFields(control);
            }
        });
    }

    private updateValidPriceRange(): void {
        this.validPriceRange.month.min = this.user.permissions.channelSubMinPriceMonthly;
        this.validPriceRange.year.min = this.user.permissions.channelSubMinPriceAnnual;
    }

    private getUserMinPlanPrice(planType: string): string | number {
        switch (planType) {
            case 'MONTHLY':
                return this.user.permissions.channelSubMinPriceMonthly ? this.user.permissions.channelSubMinPriceMonthly : 0;
            case 'YEARLY':
                return this.user.permissions.channelSubMinPriceAnnual ? this.user.permissions.channelSubMinPriceAnnual : 0;
            default:
            return 0.01;
        }
    }

    private clearModalData(): void {
        this.subscriptionsObject = [];
        this.streamTypes = [];
        this.addressLocation = {};
        this.enableCustomUrl = false;
        this.resetStreamType();
        this.streamForm?.reset();
    }
}
